import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useDeleteSurvey } from "@/features/survey/hooks/useMutation/useDeleteSurvey";
import { useNavigate, useParams } from "react-router-dom";


import { UnderlineLink } from "@/components/atoms/UnderlineLink/UnderlineLink";
import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import { Button } from '@/components/ui/button';
import {
    Menubar,
    MenubarContent,
    MenubarItem,
    MenubarMenu,
    MenubarSeparator,
    MenubarTrigger
} from "@/components/ui/menubar";
import { CheckProjectAccess } from '@/features/rbac/components/molecules/CheckProjectAccess';
import { useUpdateSurvey } from '@/features/survey/hooks/useMutation/useUpdateSurvey';
import { useSaveSurveyAsTemplate } from '@/features/survey_bank/hooks/useMutation/useSaveSurveyAsTemplate';
import { CopySurveyDialog } from "@/features/surveys/components/organisms/CopySurveyDialog";
import { useProjectStore, useSurveyStore } from "@/lib/zustand/useStore";
import { Archive, ArchiveRestore, ClipboardCopy, ClipboardPlus, FileDown, Info, List, Loader, Pencil, Settings, Trash2 } from "lucide-react";
import { useState } from 'react';

export const SurveyHeaderButtons = () => {
    const { orgId, projectId, surveyId } = useParams();
    // const location = useLocation();
    // const segments = location.pathname.split('/');
    // const lastSegmentOfURL = segments[segments.length - 1];
    const navigate = useNavigate();

    /*
    * Handle Archive, Restore and Delete Survey
    */
    const { isPending: isDeletingSurvey } = useDeleteSurvey();


    /* 
     * Copy survey dialog state 
    */
    const [openCopySurveyModal, setOpenCopySurveyModal] = useState(false);
    const { project } = useProjectStore();
    const { survey } = useSurveyStore();

    /* 
    *  Save survey as template
    */
    const { mutateAsync: saveSurveyAsTemplate, isPending } = useSaveSurveyAsTemplate();
    const saveAsTemplateHandler = async () => {
        if (!surveyId) {
            ErrorPopup("Survey ID is not available. Please try again later.");
            return;
        }
        saveSurveyAsTemplate({ surveyId }).then(() => {
            SuccessPopup("Survey saved as template successfully!");
        }).catch((error) => {
            if (error instanceof Error) {
                console.error(error);
                ErrorPopup(error.message)
            }
            else {
                ErrorPopup("An unexpected error occurred while saving the survey as a template.");
            }
        });
    }
    /* 
    * Archive/Restore survey
    */
    const { mutateAsync: updateSurvey, isPending: isUpdatingSurvey } = useUpdateSurvey();

    const archiveSurveyHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Archive this survey?", confirmButtonText: "Archive", message: 'You will be able to restore this survey later' });
        if (!userDecision.isConfirmed) return;
        try {
            await updateSurvey({ data: { id: survey_id, archived: true } });
            SuccessPopup("Survey archived successfully");
        } catch (error) {
            ErrorPopup("Error archiving survey");
        }
    };
    const restoreSurveyHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Restore this survey?", confirmButtonText: "Restore", message: 'You will be able to archive this survey later' });
        if (!userDecision.isConfirmed) return;
        try {
            await updateSurvey({ data: { id: survey_id, archived: false } });
            SuccessPopup("Survey restored successfully");
        } catch (error) {
            ErrorPopup("Error restoring survey");
        }
    };
    /* 
    * Delete survey
    */
    const { mutateAsync: deleteSurvey } = useDeleteSurvey();

    const deleteSurveyHandler = async (survey_id: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this survey? Once deleted, you will not be able to recover this survey' });
        if (!userDecision.isConfirmed) return;

        try {
            await deleteSurvey(survey_id);
            SuccessPopup("Survey deleted successfully");
            navigate(`/organization/${orgId}/project/${projectId}/manage-project`);
        } catch (error) {
            ErrorPopup("Error deleting survey");
        }
    };
    if (!orgId || !projectId || !surveyId || !survey) {
        return null;
    }
    return (
        <div>
            <CopySurveyDialog
                show={openCopySurveyModal}
                onHide={() => setOpenCopySurveyModal(false)}
                title="Copy Survey To Project"
                description="Select a project to copy the survey to."
                orgId={Number(orgId)}
                crmSystem={survey?.crm?.crm_system || ''}
                copiedSurvey={survey}
                copiedFromProject={project}
            />
            {(isUpdatingSurvey || isDeletingSurvey) && (<LoaderWithBackdrop />)}
            <Menubar>
                <MenubarMenu>
                    <MenubarTrigger asChild className='flex gap-2'>
                        <UnderlineLink to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey`}>
                            <Info size={16} /> Survey details
                        </UnderlineLink>
                    </MenubarTrigger>
                </MenubarMenu>
                <MenubarMenu>
                    <MenubarTrigger asChild className='flex gap-2'>
                        <UnderlineLink to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/questions`}>
                            <List size={16} /> Survey Questions
                        </UnderlineLink>
                    </MenubarTrigger>
                </MenubarMenu>
                <MenubarMenu>
                    <CheckProjectAccess any survey_update survey_delete survey_create>
                        <MenubarTrigger asChild className='flex gap-2'>
                            <div className="text-xs">
                                <Settings size={16} /> Options
                            </div>
                        </MenubarTrigger>
                    </CheckProjectAccess>
                    <MenubarContent align='end'>
                        <MenubarItem asChild className='flex gap-2'>
                            <UnderlineLink to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/export`}>
                                <FileDown size={16} /> Export survey
                            </UnderlineLink>
                        </MenubarItem>
                        <MenubarSeparator />
                        <CheckProjectAccess survey_create>
                            <MenubarItem asChild className='flex gap-2' onClick={() => setOpenCopySurveyModal(true)}>
                                <div className="text-xs">
                                    <ClipboardCopy size={16} />
                                    Copy survey to project
                                </div>
                            </MenubarItem>
                            <MenubarItem asChild className='flex gap-2' onClick={saveAsTemplateHandler}>
                                <div className="text-xs">
                                    <ClipboardPlus size={16} />
                                    Save as template {isPending && (<Loader size="16" className="animate-spin" />)}
                                </div>
                            </MenubarItem>
                        </CheckProjectAccess>
                        <MenubarSeparator />
                        <CheckProjectAccess survey_update>
                            <MenubarItem asChild className='flex gap-2' >
                                <UnderlineLink to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/edit`}>
                                    <Pencil size={16} />Edit
                                </UnderlineLink>
                            </MenubarItem>
                            <MenubarSeparator />
                            {survey?.archived ? (
                                <MenubarItem asChild className='flex justify-start w-full gap-2' >
                                    <Button className="text-xs" onClick={() => restoreSurveyHandler(surveyId)} variant='accent' size='sm'>
                                        <ArchiveRestore size={16} />Restore
                                    </Button>
                                </MenubarItem>
                            ) : (
                                <MenubarItem asChild className='flex justify-start w-full gap-2' >
                                    <Button className="text-xs" onClick={() => archiveSurveyHandler(surveyId)} variant='accent' size='sm'>
                                        <Archive size={16} />Archive
                                    </Button>
                                </MenubarItem>
                            )}
                        </CheckProjectAccess>
                        <CheckProjectAccess survey_delete>
                            <MenubarItem asChild className='flex justify-start w-full gap-2 mt-1 text-xs' >
                                <Button onClick={() => deleteSurveyHandler(surveyId)} variant='destructive' size='sm'>
                                    <Trash2 size={16} /> Delete
                                </Button>
                            </MenubarItem>
                        </CheckProjectAccess>
                    </MenubarContent>
                </MenubarMenu>
            </Menubar>
        </div >
    )
}
